import { getSasSkiTreeSkills } from '../data/skills/sasSkiTree';
import { normalizeSportName } from './drillTemplates';

// Pass 77 — deriveFocusSkills is HUMAN-EXPLICIT ONLY. AI authority (aiReview.focusAreas / knowledgeMatches) and
// loose free-text keyword fallbacks (timestamp-comment keyword matching) were REMOVED. Only explicitly, canonically
// linked human evidence contributes: `video.focusSkills` (instructor-selected canonical refs) and
// `reviewDrills.relatedSkillIds` (explicit canonical skill links). This surface feeds the My Skills roadmap DISPLAY;
// the authoritative progression ranking lives in progressionEvidence.js.

function incrementSkillSignal(map, skill, source, weight) {
  if (!skill) return;

  const current = map.get(skill.skillId) || {
    skillId: skill.skillId,
    treeId: skill.treeId,
    name: skill.name,
    groupType: skill.groupType,
    levelGroup: skill.levelGroup,
    source,
    score: 0,
  };

  current.score += weight;
  const sourcePriority = { instructor_selected: 5, drill: 4 };
  if ((sourcePriority[source] || 0) > (sourcePriority[current.source] || 0)) {
    current.source = source;
  }

  map.set(skill.skillId, current);
}

function getApplicableSkills(video) {
  const normalizedSport = normalizeSportName(video?.sport);
  if (normalizedSport !== 'skiing') {
    return [];
  }

  return getSasSkiTreeSkills().filter((skill) => skill.sport === normalizedSport);
}

export function getAvailableSkillsForVideo(video) {
  return getApplicableSkills(video);
}

export function resolveFocusSkillReferences(video, focusSkills = []) {
  const skills = getApplicableSkills(video);

  return (focusSkills || [])
    .map((item) => {
      const match = skills.find(
        (skill) => skill.skillId === item.skillId && (!item.treeId || skill.treeId === item.treeId)
      );

      if (!match) return null;

      return {
        ...item,
        skillId: match.skillId,
        treeId: match.treeId,
        name: match.name,
        groupType: match.groupType,
        levelGroup: match.levelGroup,
        description: match.description,
        source: item.source || 'instructor_selected',
      };
    })
    .filter(Boolean);
}

// Pass 77 — `feedbackItems` is accepted for signature compatibility but is NO LONGER used to derive focus skills
// (free-text keyword inference removed). Only explicit human canonical links contribute.
export function deriveFocusSkills(video, feedbackItems = []) { // eslint-disable-line no-unused-vars
  const skills = getApplicableSkills(video);
  if (skills.length === 0) {
    return [];
  }

  const signalMap = new Map();
  const explicitFocusSkills = resolveFocusSkillReferences(video, video?.focusSkills || []);
  const reviewDrills = video?.reviewDrills || [];

  explicitFocusSkills.forEach((skill) => {
    const match = skills.find((item) => item.skillId === skill.skillId);
    incrementSkillSignal(signalMap, match, 'instructor_selected', 10);
  });

  reviewDrills.forEach((drill) => {
    // relatedSkillIds may be explicit string ids OR { skillId, treeId } objects — both are EXPLICIT canonical
    // human links (not free-text inference).
    (drill.relatedSkillIds || [])
      .map((skillRef) => {
        const refId = typeof skillRef === 'string' ? skillRef : skillRef?.skillId;
        const refTree = typeof skillRef === 'string' ? null : skillRef?.treeId;
        return skills.find((skill) => skill.skillId === refId && (!refTree || skill.treeId === refTree));
      })
      .filter(Boolean)
      .forEach((skill) => incrementSkillSignal(signalMap, skill, 'drill', 5));
  });

  return Array.from(signalMap.values())
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    .slice(0, 4);
}
